import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { useClinicData } from "../../../clinic/clinicHooks";
import { useTranslate } from "../../../hooks/useTranslate";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ShareData, useGetShareData } from "../../share/useNativePatientShare";
import { useConversationDriver } from "../../../driver/ConversationDriverProvider";
import { isProduction } from "fe-common/utils/envGetter";
//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PdfSummaryProps {
  open?: boolean;
  onClose?: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const CASES_API = isProduction()
  ? "https://api.kahun.com/"
  : "https://api-dev.kahun.com/";

export const PdfSummary = ({ open, onClose, ...props }: PdfSummaryProps) => {
  const [shouldDownload, setShouldDownload] = useState(open);

  const [loading, setLoading] = useState(true);

  const generatingPdf = useRef(false);

  const { name: clinicName, clinic: clinicId } = useClinicData();

  useEffect(
    () =>
      evt({
        category: CATEGORY.PATIENT_RESULT,
        action: ACTION.PATIENT_WHATS_NEXT,
        label: `Save assessment clicked`,
        props: {
          clinicName,
          clinicId,
        },
      }),
    [clinicName, clinicId],
  );

  useEffect(() => {
    setShouldDownload(open);
    if (generatingPdf.current) generatingPdf.current = false;
  }, [open]);

  const { clinic } = useClinicData();

  const { locale } = useTranslate();
  const driver = useConversationDriver();
  const getShareData = useGetShareData();

  const shareData = useRef<ShareData>(null);

  useEffect(() => {
    if (!shareData.current) {
      setLoading(true);
      getShareData()
        .then((res) => (shareData.current = res))
        .finally(() => setLoading(false));
    }
  }, [getShareData, setLoading]);

  const caseId = driver.getCaseId();
  const downloadPdf = useCallback(async () => {
    const response = await fetch(
      `${CASES_API}pdf/summary/${caseId}/${clinic}?lang=${locale}`,
    );
    const blob = await response.blob();
    saveAs(blob, "summary" + new Date().toLocaleString());
  }, [locale, caseId, clinic]);

  useEffect(() => {
    if (!loading && shouldDownload && !generatingPdf.current) {
      generatingPdf.current = true;
      // console.log("generating pdf...");
      downloadPdf()
        .catch((e) => console.error(e))
        .finally(() => {
          onClose?.();
        });
    }
  }, [shouldDownload, loading, downloadPdf, onClose]);

  if (loading || generatingPdf.current) {
    // Wait for the data to come
    return <ThreeDotsLoader />;
  }

  return null;
};
